import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { trackEvent } from '@/shared/lib/analytics';
import {
  dismissInstallPrompt,
  getInstallEducationRuntimeSnapshot,
  isEligibleInstallPromptRoute,
  isInstallPromptDismissedNow,
  resolveInstallGuideHref,
} from './runtime';

interface InstallEducationPromptProps {
  initialPathname: string;
}

export function InstallEducationPrompt({ initialPathname }: InstallEducationPromptProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const impressionKeyRef = useRef<string | null>(null);
  const [dismissed, setDismissed] = useState(() => isInstallPromptDismissedNow());

  const runtime = getInstallEducationRuntimeSnapshot();
  const isEligibleRoute = isEligibleInstallPromptRoute(location.pathname, initialPathname);
  const guideHref = resolveInstallGuideHref(runtime.platform);
  const shouldRender =
    !dismissed &&
    runtime.isMobileBrowser &&
    (runtime.platform === 'ios' || runtime.platform === 'android') &&
    isEligibleRoute;

  useEffect(() => {
    setDismissed(isInstallPromptDismissedNow());
  }, [location.pathname]);

  useEffect(() => {
    if (!shouldRender) {
      return;
    }

    const impressionKey = `${location.pathname}:${runtime.platform}`;
    if (impressionKeyRef.current === impressionKey) {
      return;
    }

    impressionKeyRef.current = impressionKey;
    trackEvent('install_prompt_impression', {
      platform: runtime.platform,
      pathname: location.pathname,
      entry_surface: location.pathname.startsWith('/clubs/') ? 'club-page' : location.pathname.slice(1),
    });
  }, [location.pathname, runtime.platform, shouldRender]);

  if (!shouldRender) {
    return null;
  }

  function handleDismiss() {
    dismissInstallPrompt();
    setDismissed(true);
    trackEvent('install_prompt_dismiss', {
      platform: runtime.platform,
      pathname: location.pathname,
    });
  }

  function handleOpenGuide() {
    trackEvent('install_prompt_click', {
      platform: runtime.platform,
      pathname: location.pathname,
      guideHref,
    });
    void navigate(guideHref);
  }

  return (
    <div className="px-[var(--page-gutter)] pt-[calc(var(--safe-area-top)+0.5rem)]" data-testid="install-education-prompt">
      <section className="rounded-[22px] border border-[rgba(64,45,14,0.10)] bg-[linear-gradient(180deg,rgba(255,249,239,0.98)_0%,rgba(255,253,248,0.95)_100%)] px-3 py-3 shadow-[0_10px_20px_rgba(62,48,23,0.05)]">
        <div className="flex flex-nowrap items-center gap-2">
          <button
            type="button"
            onClick={handleOpenGuide}
            className="inline-flex min-h-10 flex-1 items-center justify-center whitespace-nowrap rounded-full bg-[var(--foreground)] px-4 text-[12px] font-semibold tracking-[0.01em] text-white"
          >
            Установить приложение
          </button>
          <button
            type="button"
            onClick={handleDismiss}
            className="inline-flex min-h-10 shrink-0 items-center justify-center whitespace-nowrap rounded-full border border-[rgba(64,45,14,0.10)] bg-white/90 px-4 text-[12px] font-semibold tracking-[0.01em] text-[rgba(26,26,26,0.72)]"
          >
            Позже
          </button>
        </div>
      </section>
    </div>
  );
}
